import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { workspaceAPI, extractErrorCode, extractErrorMessage } from '../../services/api';
import type { InviteLookupResponse } from '../../types';

export type InviteViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; lookup: InviteLookupResponse & { status: 'ok' } }
  | { kind: 'expired' }
  | { kind: 'revoked' }
  | { kind: 'already_accepted' }
  | { kind: 'not_found' }
  | { kind: 'email_taken'; email?: string };

export interface AcceptForm {
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
}

const blankForm: AcceptForm = {
  password: '',
  first_name: '',
  last_name: '',
  phone: '',
};

const PHONE_RE = /^\+?[1-9]\d{7,14}$/;

export function useInviteAcceptPage() {
  const { token: rawToken } = useParams<{ token: string }>();
  const token = rawToken ?? '';
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [view, setView] = useState<InviteViewState>({ kind: 'loading' });
  const [form, setForm] = useState<AcceptForm>(blankForm);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof AcceptForm, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setView({ kind: 'not_found' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await workspaceAPI.lookupInvite(token);
        if (cancelled) return;
        switch (result.status) {
          case 'ok':
            setView({ kind: 'ready', lookup: result as InviteLookupResponse & { status: 'ok' } });
            break;
          case 'expired':
            setView({ kind: 'expired' });
            break;
          case 'revoked':
            setView({ kind: 'revoked' });
            break;
          case 'accepted':
            setView({ kind: 'already_accepted' });
            break;
          case 'email_taken':
            setView({ kind: 'email_taken', email: result.email });
            break;
          case 'not_found':
          default:
            setView({ kind: 'not_found' });
            break;
        }
      } catch (err: any) {
        if (cancelled) return;
        setView({ kind: 'error', message: extractErrorMessage(err, 'Could not load invite.') });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const updateField = useCallback(<K extends keyof AcceptForm>(key: K, value: AcceptForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    setSubmitError(null);
  }, []);

  const validate = useCallback((): boolean => {
    const errs: Partial<Record<keyof AcceptForm, string>> = {};
    if (form.password.length < 8) errs.password = 'Password must be at least 8 characters.';
    else if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password))
      errs.password = 'Password must include letters and numbers.';
    if (!form.first_name.trim()) errs.first_name = 'First name is required.';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required.';
    if (!PHONE_RE.test(form.phone.trim())) errs.phone = 'Enter a valid phone number (e.g. +61400000000).';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }, [form]);

  const submit = useCallback(async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const resp = await workspaceAPI.acceptInvite({
        token,
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
      });
      localStorage.setItem('token', resp.access_token);
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const code = extractErrorCode(err);
      const msg = extractErrorMessage(err, 'Could not accept invite.');
      switch (code) {
        case 'invite_expired':
          setView({ kind: 'expired' });
          break;
        case 'invite_revoked':
          setView({ kind: 'revoked' });
          break;
        case 'invite_already_accepted':
          setView({ kind: 'already_accepted' });
          break;
        case 'email_already_user':
          setView({ kind: 'email_taken' });
          break;
        default:
          setSubmitError(msg);
          break;
      }
    } finally {
      setSubmitting(false);
    }
  }, [form, navigate, refreshUser, token, validate]);

  return {
    view,
    form,
    fieldErrors,
    submitting,
    submitError,
    updateField,
    submit,
    goToLogin: (emailHint?: string) => {
      const qs = emailHint ? `?email=${encodeURIComponent(emailHint)}` : '';
      navigate(`/login${qs}`);
    },
  };
}

